"use strict";

import LayerGroup from 'ol/layer/Group'
import Collection from 'ol/Collection';
import TileLayer from "ol/layer/Tile";
import ImageLayer from "ol/layer/Image";
import ImageWMS from "ol/source/ImageWMS";
import TileWMS from 'ol/source/TileWMS';
import WMTS from "ol/source/WMTS";
import WFS from "ol/format/WFS";
import VectorLayer from 'ol/layer/Vector';
import WMTSTileGrid from "ol/tilegrid/WMTS";
import Vector from "ol/source/Vector";


/**
 * @classdesc
 * Siehe {@link gc.gis.map.GCAbstractLayerFactory}.
 *
 * @class
 * @extends gc.gis.map.GCAbstractLayerFactory<Layer,LayerGroup>
 * @memberof gc.gis
 * @author Anton Popov <a.popov@brain-scc.de>
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {Object} options Aufrufoptionen (siehe auch {@link gc.gis.map.GCAbstractLayerFactory})
 */
gc.gis.OLLayerFactory = function( options )
{
	gc.gis.map.GCAbstractLayerFactory.call( this, options );
};
gc.gis.OLLayerFactory.prototype = Object.create( gc.gis.map.GCAbstractLayerFactory.prototype );


/**
 * Erstellt eine Gruppenlayerinstanz der Kartenbibliothek.
 *
 * @public
 * @method
 * @param {gc.gis.map.GCLayerTreeManager.GroupNode} groups
 * @return {G}
 */
gc.gis.OLLayerFactory.prototype.createGroupLayer = function(groups)
{
	const ol_group = new LayerGroup({
		opacity: groups.opacity
	});
	const ol_layers = [];

	for (let i = 0; i < groups.groups.length; i++)
	{
		if (groups.groups[i].layers)
		{
			ol_layers.push(this.createMergedLayer(groups.groups[i].layers));
		}
		else if (groups.groups[i].groups)
		{
			ol_layers.push(this.createGroupLayer(groups.groups[i]));
		}
	}

	ol_group.setLayers(new Collection(ol_layers));
	groups._ref.setImpl(ol_group);

	return ol_group;
};

/**
 * @inheritDoc
 */
gc.gis.OLLayerFactory.prototype.createLayer = function(gc_layer)
{
	return this.createMergedLayer([gc_layer]);
};

/**
 * @inheritDoc
 */
gc.gis.OLLayerFactory.prototype.createMergedLayer = function(gc_layers)
{
	// console.log('OLLayerFactory.createMergedLayer(): '+ gc_layers.length +' layers ... ');
	if( gc_layers.length === 0 )
	{
		return undefined;
	}

	const layer_config = {
		_ref:       [],
		layers:     [],
		styles:     [],
		isInternal: gc_layers[0].isInternal(),
        useProxy:	!gc_layers[0].isInternal(),
		type:       gc_layers[0] instanceof gc.gis.layer.GCVectorLayer ? 'vector' : 'raster',
        source: 	'wms',
		opacity:    gc_layers[0].getOpacity(),
		minScale:   gc_layers[0].getMinScale(),
		maxScale:   gc_layers[0].getMaxScale(),
		url:        gc_layers[0].getUrl(),
		format:     gc_layers[0].getFormat(),
		projection: gc_layers[0].getProjection(),
		version:    gc_layers[0].getVersion()
	};

	if( gc_layers[0] instanceof gc.gis.layer.GCRasterLayer )
	{
		layer_config.tiled = gc_layers[0].isTiled();
	}

	// spezielle Verbindungsparameter angegeben => Verbindung direkt zum Dienst und nicht über Kartenkatalog
    if (/*gc_layers[0].isInternal() && */gc_layers[0].hasExternalConnectionSettings())
	{
		if (gc_layers.length > 1) {
			// darf eigentlich nicht vorkommen, sollte beim Mergen berücksichtigt werden
            throw new gc.gis.error.GCError('External connection settings are not supported for merged layers.');
		}

        const settings = gc_layers[0].getExternalConnectionSettings();
        if (settings)
        {
            layer_config._ref.push(gc_layers[0]);

            layer_config.source = settings.source || 'wms';
            if (layer_config.source === 'wmts') {
                layer_config.tiled = true;

                if (settings.matrixSet) {
                    layer_config.matrixSet = settings.matrixSet;
                }
                if (settings.extent) {
                    layer_config.extent = settings.extent;
                }
                if (settings.origin) {
                    layer_config.origin = settings.origin;
                }
                if (settings.resolutions) {
                    layer_config.resolutions = settings.resolutions;
                }
                if (settings.matrixIds) {
                    layer_config.matrixIds = settings.matrixIds;
                }
                if (settings.requestEncoding) {
                    layer_config.requestEncoding = settings.requestEncoding;
                }
            }
            if (settings.layers) {
                layer_config.layers = settings.layers;
            }
            if (settings.styles) {
                layer_config.styles = settings.styles;
            }
            if (settings.url) {
                layer_config.url = settings.url;
            }
            if (settings.version) {
                layer_config.version = settings.version;
            }
            if (settings.format) {
                layer_config.format = settings.format;
            }

            layer_config.useProxy = !(settings.disableProxy && gc.gis.iface.isFE());
        }

		return this._createOLLayer(layer_config);
	}

	const layerNames = [];
	const styleNames = [];
	for (let i = 0, ii = gc_layers.length; i < ii; i++)
	{
		layer_config._ref.push(gc_layers[i]);
		layerNames.push(gc_layers[i].getName());

		if (gc_layers[i] instanceof gc.gis.layer.GCRasterLayer)
		{
			styleNames.push(gc_layers[i].getStyle() || '');
		}
	}
	layer_config.layers = layerNames.join(',');
	layer_config.styles = styleNames.join(',');

	if (gc_layers.length === 1 && !gc_layers[0].isInternal())
	{
		try {
			layer_config.extent = gc_layers[0].getExtentSync();
		}
		catch (err)
		{
			gc.gis.iface.log.error(self, err);
		}
	}

	// console.log(layer_config);
	return this._createOLLayer(layer_config);
};

/**
 * @public
 * @method
 * @param {Object} layer von {@link gc.gis.OLLayerFactory.prototype.createMergedLayer} gemergte Layerkonfiguration
 * @param {string|boolean} layer.isInternal
 * @param {string} layer.type 'raster' für Rasterlayer, 'vector' für Vektorlayer
 * @param {boolean} [layer.tiled] nur bei Raster-Layern
 * @param {string} layer.url
 * @param {string} layer.projection
 * @param {string} layer.format
 * @param {number} layer.opacity
 * @param {number} layer.minScale
 * @param {number} layer.maxScale
 * @param {string} [layer.version]
 * @param {string} layer.layers <code>LAYERS</code>-Parameter für WMS bzw. <code>TYPENAME</code>-Parameter für WFS
 * @param {gc.gis.layer.GCLayer[]} layer._ref <strong>Wichtig:</strong> zugehörige GC-Layerinstanzen
 * @return Layer
 */
gc.gis.OLLayerFactory.prototype._createOLLayer = function( layer )
{
	const self = this;
	let ol_layer;

	if (layer.type === 'raster')
	{
		if (layer.tiled !== true)
		{
			ol_layer = this._createSingleImageLayer(layer);
		}
		else
		{
			ol_layer = this._createTileImageLayer(layer);
		}
	}
	else if (layer.type === 'vector')
	{
		ol_layer = this._createVectorLayer(layer);
	}
	else
	{
		throw new gc.gis.error.GCError('Unsupported layer type: '+ layer.type);
	}

	if (ol_layer)
	{
		// Layerreferenzen setzen.
		for(let i = 0; i < layer._ref.length; i++)
		{
			layer._ref[i].setImpl(ol_layer);
			layer._ref[i].on( gc.gis.layer.GCLayerEvent.OPACITY_CHANGED, self, function(evt ) {
				ol_layer.setOpacity(evt.newValue);
			});
		}
		ol_layer._ref = layer._ref;
	}

	return ol_layer;
};

/**
 * @public
 * @method
 * @param {Object} options Layerkonfiguration
 * @return {TileLayer}
 */
gc.gis.OLLayerFactory.prototype._createTileImageLayer = function(options)
{
	const self = this;
    let ol_source;

    if (options.source === 'wmts')
    {
        ol_source = this._createWMTSSource(options);
    }
    else
    {
        ol_source = this._createTileWMSSource(options);
    }

    ol_source.on('tileloadstart', function(evt) {
    	self._map.onLoadingStarted(options._ref);
    });
	ol_source.on('tileloadend', function(evt) {
		self._map.onLoadingEnded(options._ref);
	});
	ol_source.on('tileloaderror', function(evt) {
		self._map.onLoadingError(options._ref, evt);
	});

	return new TileLayer({
		opacity:  options.opacity,
		minScale: options.minScale,
		maxScale: options.maxScale,
		source:   ol_source
	});
};

/**
 * @public
 * @method
 * @param {Object} options Layerkonfiguration
 * @return {ImageLayer} Layerinstanz
 */
gc.gis.OLLayerFactory.prototype._createSingleImageLayer = function(options)
{
	const self = this;
    const ol_source = this._createImageWMSSource(options);

	ol_source.on('imageloadstart', function(evt) {
		self._map.onLoadingStarted(options._ref);
	});
	ol_source.on('imageloadend', function(evt) {
		self._map.onLoadingEnded(options._ref);
	});
	ol_source.on('imageloaderror',function(evt) {
		self._map.onLoadingError(options._ref, evt);
	});

	let imageLayerOptions = {
		opacity:  options.opacity,
		minScale: options.minScale,
		maxScale: options.maxScale,
		source:   ol_source
	};

	if (typeof options.extent !== 'undefined')
	{
		imageLayerOptions.extent = options.extent;
	}

	return new ImageLayer(imageLayerOptions);
};


/**
 * @public
 * @method
 * @param {Object} options Layerkonfiguration
 * @return {ImageWMS}
 */
gc.gis.OLLayerFactory.prototype._createImageWMSSource = function (options)
{
	const source_options = {
		url: Array.isArray(options.url) ? options.url[0] : options.url,
		ratio: 1.1,
		// crossOrigin: undefined,
		params: {
			'LAYERS': options.layers,
			'STYLES':  options.styles || '',
			// 'VERSION': options.version,
			'CRS': options.projection,
			'FORMAT': options.format
		}
	};

	if(options.useProxy)
	{
		source_options.imageLoadFunction = gc.gis.OLLayerFactory.proxyImageLoadFunction;
	}

    return new ImageWMS(source_options);
};


/**
 * @public
 * @method
 * @param {Object} options Layerkonfiguration
 * @return {TileWMS}
 */
gc.gis.OLLayerFactory.prototype._createTileWMSSource = function (options)
{
	const source_options = {
		// crossOrigin: true,
		params: {
			'LAYERS': options.layers,
			'STYLES':  options.styles,
			// 'VERSION': options.version,
			'CRS': options.projection,
			'FORMAT': options.format
		}
	};

	if (Array.isArray(options.url))
	{
		source_options.urls = options.url;
	}
	else
	{
		source_options.url = options.url;
	}

	if(options.useProxy)
	{
		source_options.tileLoadFunction = gc.gis.OLLayerFactory.proxyImageLoadFunction;
	}

    return new TileWMS(source_options);
};


/**
 * @public
 * @method
 * @param {Object} options Layerkonfiguration
 * @return {WMTS}
 */
gc.gis.OLLayerFactory.prototype._createWMTSSource = function (options)
{
    let source_options = {};

	const grid_options = {
		resolutions: options.resolutions,
		matrixIds: options.matrixIds
	};
	if (options.extent) {
		grid_options.extent = options.extent;
	}
	else {
		grid_options.origin = options.origin;
	}
	source_options = {
		layer: options.layers,
		style: options.styles,
		matrixSet: options.matrixSet,
		format: options.format || 'image/png',
		requestEncoding: options.requestEncoding || 'KVP', // 'KVP' oder 'REST'
		tileGrid: new WMTSTileGrid(grid_options),
		projection: options.projection
	};

	if (Array.isArray(options.url))
	{
		source_options.urls = options.url;
	}
	else
	{
		source_options.url = options.url;
	}

	if(options.useProxy)
	{
		source_options.tileLoadFunction = gc.gis.OLLayerFactory.proxyImageLoadFunction;
	}

	return new WMTS(source_options);
};

/**
 * @public
 * @method
 * @param {Object} options Layerkonfiguration
 * @return {VectorLayer} Layerinstanz
 */
gc.gis.OLLayerFactory.prototype._createVectorLayer = function( options )
{
	// einmal geladen, immer geladen
	if(options._ref && options._ref.length > 0 && options._ref[0].getImpl() instanceof VectorLayer)
	{
		return options._ref[0].getImpl();
	}

	const self = this;

	// basic options
	const config = {
		opacity: options.opacity/*,
		visible: options.visible*/
	};

	// resolution
	if (options.minScale)
	{
		config.minResolution = options.minScale;
	}
	if (options.maxScale)
	{
		config.maxResolution = options.maxScale;
	}

	// source
	const config_source = {};

	if(options.attribution)
	{
		config_source.attributions = options.attribution;
	}

	const format = new WFS();

	config_source.loader = function(extent, resolution, projection) {
		self._map.onLoadingStarted( options._ref );

		let url = options.url;
		if(url.indexOf('?') >= 0)
		{
			// alles nach dem '?' wegschmeißen
			url = url.replace(/\?.*$/g,'');
		}

		const wfs_params = {
			SERVICE: 'WFS',
			VERSION: options.version,
			REQUEST: 'GetFeature',
			SRSNAME: self._map.getProjection(),
			TYPENAME: options.layers
			//BBOX: extent.join(',') +','+ self._map.getProjection()
		};
		url = gc.gis.GCUrl.buildUrl(url, wfs_params);

		//gc.gis.iface.log.debug(this, 'OLRequestManager: Using proxy for external WFS. URL: ' + url );

		let i;
		gc.gis.iface.request.ajax({
			label: 'Lade Vektordaten ... ',
			context: self,
			url: url,
			useOGCProxy: true,
			success: function(data)
			{
                let features = [];
                if (self._map.getProjection() === 'EPSG:4326')
                {
                    features = format.readFeatures(data, {
                        dataProjection: 'CRS:84',
                        featureProjection: 'EPSG:4326'
                    });
                    for (i=0; i<features.length; i++)
                    {
                    	// TODO Achsenorientierung auch für andere CRS beachten
                        const geom = features[i].getGeometry();
                        const c = geom.getCoordinates();
                        geom.setCoordinates(gc.gis.GCUtil.flipAxis(c, 3));
                    }
                }
                else
                {
                    features = format.readFeatures(data);
                }

                config.source.addFeatures(features);
                for(i=0; i<options._ref.length; i++)
                {
                    options._ref[i].setExtent(config.source.getExtent());
                }

                gc.gis.iface.log.debug( this, 'WFS: Features loaded: '+ features.length );
            },
            finally: function()
            {
                self._map.onLoadingEnded( options._ref );
			},
			error: function( err )
			{
				self._map.onLoadingError( options._ref, err );
			}
		});
	};

	config.source = new Vector(config_source);

	// TODO anderer Style
	config.style = gc.gis.iface.styleFactory.getStyle(gc.gis.factory.StyleType.WFS);

	// create layer
	return new VectorLayer(config);
};

/**
 * @param {Image} image Image or tile Image.
 * @param {string} src Source.
 */
gc.gis.OLLayerFactory.proxyImageLoadFunction = function(image, src)
{
	// gc.gis.iface.log.debug(this, 'Using proxy for external URL: ' + src);
	src = gc.gis.GCUrl.buildUrl(gc.gis.iface.requests.ogcProxyUrl, {url: src});

	// OL führt selbst keine Requests aus, sondern überträgt diese Aufgabe an den Browser (image.src = src),
	// hier ist dies nicht möglich, da ein spezieller HTTP-Header gesetzt werden muss
	const client = new XMLHttpRequest();
	client.open('GET', src);
	client.onload = function () {
		image.getImage(). src = window.URL.createObjectURL(this.response);
	};
	client.responseType = 'blob';
	client.setRequestHeader("X-GC-Token", gc.gis.iface.requests.ogcProxyToken);
	client.send();
};

